import type { ReactNode } from "react";
import { ChevronRight } from "../../shared/Icon";
import { cn } from "../../../lib/cn";

/**
 * Small building blocks shared by the activity cards: the neutral icon chip,
 * a CSS-only collapse, a chevron that rotates with open state, and the mono
 * detail block used for inputs / results / errors.
 */

/** Neutral square that holds a tool's action glyph. Color stays neutral — the
 * StatusBadge carries the only semantic color on a card (design rule #3). */
export function IconChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-bg-inset text-text-secondary shrink-0">
      {children}
    </span>
  );
}

/** Chevron affordance that points right when closed, down when open. */
export function Disclosure({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "text-text-quaternary transition-transform duration-fast ease-out shrink-0",
        open && "rotate-90",
      )}
    >
      <ChevronRight size={14} />
    </span>
  );
}

/** CSS grid-rows collapse (animates height, respects reduced motion). Pass
 * children lazily (only when opened) to avoid mounting heavy hidden content. */
export function Collapse({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div className="collapse-grid" data-open={open ? "true" : "false"}>
      <div className="collapse-grid-inner">{children}</div>
    </div>
  );
}

/** Monospace detail block for tool input / result / error bodies. */
export function MonoBlock({
  children,
  tone = "default",
  label,
  className,
}: {
  children: string;
  tone?: "default" | "error";
  label?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      {label && (
        <div className="text-caption text-text-quaternary mb-1 uppercase tracking-wide">
          {label}
        </div>
      )}
      <pre
        className={cn(
          "overflow-auto rounded-sm px-2.5 py-2 font-mono text-[12px] leading-relaxed",
          "whitespace-pre-wrap break-all max-h-[34vh]",
          tone === "error"
            ? "bg-status-error-soft text-text-primary"
            : "bg-bg-inset text-text-primary",
        )}
      >
        {children}
      </pre>
    </div>
  );
}
